import * as React from "react";
import { Button } from "@project-stories/elements/Button/Button";
import {Paragraph} from "@arctheme-components/elements/Paragraph/Paragraph";
import {getPaddingButton} from "./TileMethods";
import { useHubSpotForm } from "@src/hooks/useHubSpotForm";

const SmallImage = ({ imageUrl, height, altText }) => {
    if (!imageUrl) return null;
    
    return (
        <img 
            src={imageUrl} 
            alt={altText || "Small image"} 
            style={{ 
                height: `${height}px`, 
                width: "auto", 
                objectFit: "contain"
            }}
            loading="lazy"
        />
    );
};

const TileContent = ({ data, isMobile, globals }) => {
    const buttonWidth = isMobile ? data.settings.button_width_mobile : data.settings.button_width;
    const paddingContent = getPaddingButton(data);
    const taglineStyle = isMobile
        ? data.settings.tagline_style_mobile
        : data.settings.tagline_style;
    
    // Small image settings
    const smallImage = data.settings?.small_image;
    const smallImagePosition = data.settings?.small_image_position || "next_to_title";
    const smallImageHeight = data.settings?.small_image_height || 40;
    const smallImageGap = data.settings?.small_image_gap || 10;
    
    // Initialize HubSpot form for contact buttons
    const { openForm } = useHubSpotForm({
        portalId: globals.hubspot.portal_id,
        formId: data?.settings?.section_hubspot_form_id ? data?.settings?.section_hubspot_form_id : globals.hubspot.form_id, 
        region: data?.settings?.section_hubspot_region ? data?.settings?.section_hubspot_region: globals.hubspot.form_region
    });

    const handleContactClick = () => {
        openForm();
    };

    // Check if this is a contact form button
    const isContactForm = data.settings.use_form;

    return (
        <div className={`flex flex-col gap-[${data.settings.content_gap}px]`}>
            {data.settings?.tagline && (
                <Paragraph
                    text={data.settings?.tagline}
                    type={`flex justify-${data.settings.title_alignment_mobile}
                     lg:justify-${data.settings.title_alignment_desktop}
                     ${taglineStyle}`}
                    maxChars={data.settings.readmore_max_chars}
                />
            )}

            {/* Small image above title */}
            {smallImage && smallImagePosition === "above_title" && (
                <div 
                    className={`flex justify-${data.settings.title_alignment_mobile} 
                        lg:justify-${data.settings.title_alignment_desktop} 
                        mb-[${smallImageGap}px]`}
                >
                    <SmallImage 
                        imageUrl={smallImage} 
                        height={smallImageHeight} 
                        altText={data.settings?.title?.replace(/<\/?[^>]+(>|$)/g, "")}
                    />
                </div>
            )}

            {data.settings?.title && !data.settings?.use_counter && !smallImage && (
                <Paragraph
                    text={data.settings?.title}
                    type={
                        `flex justify-${data.settings.title_alignment_mobile}
                        lg:justify-${data.settings.title_alignment_desktop}
                        text-${data.settings.title_alignment_mobile}
                        lg:text-${data.settings.title_alignment_desktop}
                ${isMobile
                            ? data.settings.title_font_type_mobile
                            : data.settings.title_font_type}
                `
                    }
                    classes={`tile__title`}
                    maxChars={data.settings.readmore_max_chars}
                />
            )}
            
            {/* Title with small image to the left/right */}
            {data.settings?.title && !data.settings?.use_counter && smallImage && smallImagePosition === "next_to_title" && (
                <div className={`
                    flex items-center gap-[${smallImageGap}px]
                    justify-${data.settings.title_alignment_mobile}
                    lg:justify-${data.settings.title_alignment_desktop}
                `}>
                    <SmallImage 
                        imageUrl={smallImage} 
                        height={smallImageHeight} 
                        altText={data.settings?.title?.replace(/<\/?[^>]+(>|$)/g, "")}
                    />
                    
                    <Paragraph
                        text={data.settings?.title}
                        type={
                            `flex justify-${data.settings.title_alignment_mobile}
                            lg:justify-${data.settings.title_alignment_desktop}
                            text-${data.settings.title_alignment_mobile}
                            lg:text-${data.settings.title_alignment_desktop}
                            ${isMobile
                                ? data.settings.title_font_type_mobile
                                : data.settings.title_font_type}
                            `
                        }
                        classes={`tile__title`}
                        maxChars={data.settings.readmore_max_chars}
                    />
                </div>
            )}

            {data.settings?.title && !data.settings?.use_counter && smallImage && smallImagePosition !== "next_to_title" && (
                <Paragraph
                    text={data.settings?.title}
                    type={
                        `flex justify-${data.settings.title_alignment_mobile}
                        lg:justify-${data.settings.title_alignment_desktop}
                        text-${data.settings.title_alignment_mobile}
                        lg:text-${data.settings.title_alignment_desktop}
                        ${isMobile
                            ? data.settings.title_font_type_mobile
                            : data.settings.title_font_type}
                        `
                    }
                    classes={`tile__title`}
                    maxChars={data.settings.readmore_max_chars}
                />
            )}

            {data.settings?.title && data.settings?.use_counter && (
                <>
                    <div
                        x-data={`textcounter({ targetNumber: ${parseFloat(
                            data.settings?.title
                                .replace(/,/g, "")
                                .replace(/<\/?[^>]+(>|$)/g, ""),
                        )}, duration: 5000 })`}
                        x-ref="counterElement"
                    >
                        <p
                            x-text="formattedNumber"
                            className={
                                isMobile
                                    ? data.settings.title_font_type_mobile
                                    : data.settings.title_font_type
                            }
                        ></p>
                    </div>
                </>
            )}

            {/* Small image below title */}
            {smallImage && smallImagePosition === "below_title" && (
                <div 
                    className={`flex justify-${data.settings.title_alignment_mobile} 
                        lg:justify-${data.settings.title_alignment_desktop} 
                        mt-[${smallImageGap}px]`}
                >
                    <SmallImage 
                        imageUrl={smallImage} 
                        height={smallImageHeight} 
                        altText={data.settings?.title?.replace(/<\/?[^>]+(>|$)/g, "")}
                    />
                </div>
            )}

            {data.settings.subtitle && (
                <h3
                    className={`flex justify-${data.settings.text_content_alignment_mobile}
                        lg:justify-${data.settings.text_content_alignment_desktop}
                        text-${data.settings.text_content_alignment_mobile}
                        lg:text-${data.settings.text_content_alignment_desktop}
                        ${isMobile ? data.settings.subtitle_style_mobile : data.settings.subtitle_style}`}
                >
                    {data.settings.subtitle}
                </h3>
            )}

            {data.settings?.content && (
                <Paragraph
                    text={data.settings?.content}
                    type={`
                flex
                justify-${data.settings.text_content_alignment_mobile}
                lg:justify-${data.settings.text_content_alignment_desktop}
                text-${data.settings.text_content_alignment_mobile}
                lg:text-${data.settings.text_content_alignment_desktop}
                ${isMobile ? data.settings.content_font_type_mobile : data.settings.content_font_type} 
                space-y-[${data.settings.content_gap}px]
              `}
                    maxChars={data.settings.readmore_max_chars}
                />
            )}
            {(data.settings.show_button || data.settings.show_secondary_button) && (
                <div className="flex flex-wrap gap-6 lg:gap-8">
                    {data.settings.show_button && (
                        <div className={`flex
                            justify-${data.settings.button_alignment_mobile}
                            lg:justify-${data.settings.button_alignment_desktop} ${paddingContent}`
                        }
                        >
                            {isContactForm ? (
                                <Button
                                    className={`${data.settings.button_class}`}
                                    style={data.settings?.button_style}
                                    styleType={data.settings.button_style_type}
                                    reverse={data.settings.button_reverse}
                                    text={true}
                                    label={data.settings.button_text}
                                    icon={`${data.settings.button_icon}`}
                                    buttonStyle={buttonWidth > 0 ? { width: buttonWidth } : undefined}
                                    onClick={handleContactClick}
                                />
                            ) : (
                                <Button
                                    className={`${data.settings.button_class}`}
                                    style={data.settings?.button_style}
                                    styleType={data.settings.button_style_type}
                                    reverse={data.settings.button_reverse}
                                    text={true}
                                    label={data.settings.button_text}
                                    icon={`${data.settings.button_icon}`}
                                    link={data.settings.button_url}
                                    buttonStyle={buttonWidth > 0 ? { width: buttonWidth } : undefined}
                                    onClick={undefined}
                                />
                            )}
                        </div>
                    )}
                    {data.settings.show_secondary_button && (
                        <div className={`flex
                            justify-${data.settings.button_alignment_mobile}
                            lg:justify-${data.settings.button_alignment_desktop} ${paddingContent}`
                        }
                        >
                            <Button
                                className={`${data.settings.secondary_button_class}`}
                                style={data.settings?.secondary_button_style}
                                styleType={data.settings.secondary_button_style_type}
                                reverse={data.settings.secondary_button_reverse}
                                text={true}
                                label={data.settings.secondary_button_text}
                                icon={`${data.settings.secondary_button_icon}`}
                                link={data.settings.secondary_button_url}
                                buttonStyle={buttonWidth > 0 ? { width: buttonWidth } : undefined}
                                onClick={undefined}
                            />
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default TileContent;
